import { browserTimeZone } from '../timeZones.ts';
import type { HttpClient } from '../http/HttpClient.ts';
import type { ProjectRole } from './OrgsApi.ts';

export const PROJECT_COLORS = [
  'blue',
  'teal',
  'green',
  'amber',
  'orange',
  'red',
  'pink',
  'violet',
  'slate',
] as const;
export type ProjectColor = (typeof PROJECT_COLORS)[number];

export interface Project {
  id: string;
  key: string;
  name: string;
  description: string;
  color: ProjectColor;
  archived: boolean;
  role: ProjectRole;
}

export interface ProjectMember {
  userId: string;
  name: string;
  /** Null when the viewer is an org guest (they can't see the member directory). */
  email: string | null;
  role: ProjectRole;
  viaOrgRole: boolean;
  /** Paused over the org owner's plan: can't be assigned (null when you're an org guest). */
  paused: boolean | null;
}

const p = (org: string, key?: string) =>
  `/orgs/${encodeURIComponent(org)}/projects${key ? `/${encodeURIComponent(key)}` : ''}`;

/** Projects and project membership. */
export class ProjectsApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  list(org: string, { archived = false } = {}): Promise<{ projects: Project[] }> {
    return this.#http.get(p(org), { query: archived ? { archived: 'true' } : {} });
  }

  /** New projects take the creator's time zone for sprint scheduling (changeable in settings). */
  create(
    org: string,
    input: { key: string; name: string; description?: string; color?: ProjectColor },
  ): Promise<{ project: Project }> {
    const timezone = browserTimeZone();
    return this.#http.post(p(org), timezone ? { ...input, timezone } : input);
  }

  get(org: string, key: string): Promise<{ project: Project }> {
    return this.#http.get(p(org, key));
  }

  update(
    org: string,
    key: string,
    patch: { name?: string; description?: string; color?: ProjectColor },
  ): Promise<{ project: Project }> {
    return this.#http.patch(p(org, key), patch);
  }

  archive(org: string, key: string): Promise<{ project: Project }> {
    return this.#http.post(`${p(org, key)}/archive`);
  }

  unarchive(org: string, key: string): Promise<{ project: Project }> {
    return this.#http.post(`${p(org, key)}/unarchive`);
  }

  delete(org: string, key: string, confirmKey: string): Promise<void> {
    return this.#http.request('DELETE', p(org, key), { body: { confirmKey } });
  }

  members(org: string, key: string): Promise<{ members: ProjectMember[] }> {
    return this.#http.get(`${p(org, key)}/members`);
  }

  addMember(org: string, key: string, userId: string, role: ProjectRole): Promise<void> {
    return this.#http.post(`${p(org, key)}/members`, { userId, role });
  }

  changeMemberRole(org: string, key: string, userId: string, role: ProjectRole): Promise<void> {
    return this.#http.patch(`${p(org, key)}/members/${userId}`, { role });
  }

  removeMember(org: string, key: string, userId: string): Promise<void> {
    return this.#http.delete(`${p(org, key)}/members/${userId}`);
  }
}
